/**
 * Pure list-sort engine for the operator list panels (REQ-UI-110).
 *
 * The Platforms / Detections / Alerts panels each let the operator choose a
 * **sort field + direction** (asc/desc) from a per-panel gear menu
 * (:file:`@/multi-pane/list-sort-menu.tsx`); the selection persists per-user via
 * the Profile ``list_sort`` preference. This module owns the *sorting* — a
 * single stable, null-aware :func:`sortBy` plus a small comparator kit the
 * panels build their per-field comparators from — so the ordering rules live
 * once (CLAUDE.md §4.4 — no duplicated logic) and are unit-tested in isolation
 * from the React panels.
 *
 * # Invariants
 *
 *   - **Stable.** Equal-keyed items keep their incoming relative order
 *     (``Array.prototype.sort`` is stable per the ES2019 spec, which all our
 *     targets satisfy). This matters: the panels feed an already-meaningful
 *     order (newest-first for the timelines, display-name for Platforms), and a
 *     coarse sort (by health bucket, by classification) must not scramble the
 *     within-bucket order the operator already reads.
 *   - **Nulls / undefined always last**, regardless of direction. A row missing
 *     the sort field (no classification, no confidence, no last-status time)
 *     sinks to the bottom whether the operator sorts asc or desc — an absent
 *     value is never "smallest" or "largest", it is *unknown*, and a high-consequence
 *     operator should see the known rows first either way (CLAUDE.md §8.1 —
 *     surface the data, don't bury it under blanks). This is enforced centrally
 *     in :func:`sortBy`, so individual comparators never have to special-case
 *     null.
 *   - **Non-mutating.** :func:`sortBy` returns a new array; the caller's source
 *     (a TanStack Query result, a Zustand-derived array) is never sorted in
 *     place — sorting must not disturb selection or the keep-in-view scroll
 *     (selection is keyed by id; REQ-UI-051).
 *
 * Cross-references:
 *   - REQ-UI-110 (operator list sort) / REQ-UI-051 (sort coexists with
 *     id-keyed selection) / REQ-UI-046 (health severity order).
 *   - ADR-022 (user preferences) / ADR-024 (panels) / ADR-033 (Profiles).
 *   - storyboard Flow 05 Frame 1 (health severity order: offline → degraded →
 *     unknown → healthy).
 */
import type { MarkerStatus } from "@/map/marker-styling";

/** A sort direction (matches the BFF ``SortDirectionLiteral``). */
export type SortDirection = "asc" | "desc";

/**
 * A sort selection — a field name + a direction. Field-name-generic so each
 * panel parameterises it with its own field union (the BFF ``*SortFieldLiteral``
 * unions). ``null`` (no selection) is the panel's default order, handled by the
 * caller, not here.
 */
export interface SortSelection<TField extends string> {
  readonly field: TField;
  readonly direction: SortDirection;
}

/**
 * A field comparator: an ascending-order comparison of two items. Return
 * negative when ``a`` sorts before ``b``, positive when after, 0 when equal on
 * this field. :func:`sortBy` applies the direction (flipping for ``desc``) and
 * the nulls-last rule, so a comparator only expresses the *ascending* order of
 * the **present** values — it never sees a null on the field it sorts (the
 * caller's comparator extracts the value; absent values are routed to the tail
 * before the comparator runs — see :func:`sortBy`).
 */
export type Comparator<T> = (a: T, b: T) => number;

/**
 * A per-field comparator registry — maps each of a panel's sort fields to the
 * ascending-order comparator for that field. The panel passes this to
 * :func:`sortBy` keyed by the live selection's ``field``.
 */
export type ComparatorMap<T, TField extends string> = Readonly<Record<TField, Comparator<T>>>;

/**
 * Stably sort ``items`` by ``selection`` using the per-field ``comparators``,
 * with **nulls/undefined always last** and the source left unmutated
 * (REQ-UI-110).
 *
 * The direction is applied here (the comparator expresses ascending order
 * only); a ``desc`` sort negates the comparator result. The nulls-last rule is
 * also applied here via the optional ``isAbsent`` predicate: items for which
 * ``isAbsent(item)`` is ``true`` are partitioned to the tail in their incoming
 * order *before* the comparator runs on the rest — so they sink regardless of
 * direction and the comparator never has to null-check. When ``isAbsent`` is
 * omitted (fields that are never absent — e.g. a required name or an enum int),
 * every item is "present" and the whole list is comparator-sorted.
 *
 * Stability: the present-items sort is index-tie-broken (the comparator result,
 * then the original index) so equal-keyed items keep their incoming order even
 * though we sort a decorated copy; the absent items are appended in incoming
 * order. Equivalent to a stable sort over the original array.
 *
 * @param items - The source list (not mutated).
 * @param selection - The active ``{ field, direction }`` selection.
 * @param comparators - Per-field ascending comparators; the one keyed by
 *     ``selection.field`` is used.
 * @param isAbsent - Optional predicate marking an item as missing the sort
 *     field (routed to the tail). Omit for never-absent fields.
 * @returns A new, sorted array.
 *
 * Cross-references:
 *   - REQ-UI-110 (stable, nulls-last, non-mutating).
 */
export function sortBy<T, TField extends string>(
  items: readonly T[],
  selection: SortSelection<TField>,
  comparators: ComparatorMap<T, TField>,
  isAbsent?: (item: T) => boolean,
): T[] {
  const comparator = comparators[selection.field];
  const sign = selection.direction === "desc" ? -1 : 1;

  // Decorate with the original index so we can (a) tie-break for stability and
  // (b) keep the absent partition in incoming order.
  const decorated = items.map((item, index) => ({ item, index }));

  const present: { item: T; index: number }[] = [];
  const absent: { item: T; index: number }[] = [];
  for (const entry of decorated) {
    if (isAbsent !== undefined && isAbsent(entry.item)) {
      absent.push(entry);
    } else {
      present.push(entry);
    }
  }

  present.sort((a, b) => {
    const result = comparator(a.item, b.item) * sign;
    // Index tie-break makes the sort stable even though we sort a decorated
    // copy: equal-keyed items resolve to their incoming relative order. The
    // tie-break is NOT direction-flipped — incoming order is preserved as-is.
    return result !== 0 ? result : a.index - b.index;
  });

  // Present items first (sorted), then the absent items in incoming order.
  return [...present, ...absent].map((entry) => entry.item);
}

// ---- Comparator kit ------------------------------------------------

/**
 * Case-insensitive string comparison (ascending), locale-aware. Used for names
 * + classification labels so "alpha" and "Alpha" sort together and accented
 * characters order sensibly. ``Intl``-backed via ``localeCompare`` with
 * ``sensitivity: "base"`` (case- and accent-insensitive for the *ordering*,
 * matching how an operator scans a name column).
 *
 * @param a - First string.
 * @param b - Second string.
 * @returns Negative / 0 / positive per ascending case-insensitive order.
 */
export function compareStringsCI(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/**
 * Numeric comparison (ascending). Used for confidence + counts. Both operands
 * are expected finite (the caller routes ``null`` to the absent partition via
 * :func:`sortBy`'s ``isAbsent``); a stray ``NaN`` is treated as equal to avoid
 * a non-deterministic sort (it never reorders).
 *
 * @param a - First number.
 * @param b - Second number.
 * @returns ``a - b`` (0 when either is ``NaN``).
 */
export function compareNumbers(a: number, b: number): number {
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return 0;
  }
  return a - b;
}

/**
 * ISO-8601 timestamp comparison (ascending — oldest first). Parses each string
 * to epoch ms; an unparseable timestamp compares as equal (never reorders) so a
 * malformed value can't produce a non-deterministic sort. Callers that want
 * newest-first pass ``direction: "desc"`` to :func:`sortBy` (the default for the
 * Detections / Alerts timelines), rather than a separate descending comparator.
 *
 * @param a - First ISO-8601 timestamp string.
 * @param b - Second ISO-8601 timestamp string.
 * @returns Negative / 0 / positive per ascending chronological order.
 */
export function compareIso(a: string, b: string): number {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) {
    return 0;
  }
  return ta - tb;
}

/**
 * Rank comparison (ascending by rank). A higher rank sorts *later* in ascending
 * order, so a panel pairs this with the direction that puts its most-urgent
 * value first. Used for:
 *
 *   - **Platform health** — :func:`healthSeverityRank` ranks offline (0) →
 *     degraded (1) → unknown (2) → healthy (3); ``desc`` is unused, the panel
 *     defaults the health sort to ``asc`` so the most-urgent (offline) is at the
 *     top (Flow 05 Frame 1 severity order).
 *   - **Alert priority / type** — the panel ranks by the enum int (or a label
 *     index) and sorts ``desc`` so the highest-priority is first.
 *
 * @param rankOf - Maps an item to its integer rank.
 * @returns An ascending-by-rank :type:`Comparator`.
 */
export function compareByRank<T>(rankOf: (item: T) => number): Comparator<T> {
  return (a, b) => rankOf(a) - rankOf(b);
}

/**
 * Operator severity rank for a platform :type:`MarkerStatus` (REQ-UI-046; Flow
 * 05 Frame 1). Lower rank = more urgent = sorts first in **ascending** order:
 *
 *   | status      | rank | operator meaning            |
 *   | :---------- | :--- | :-------------------------- |
 *   | unhealthy   | 0    | offline platform (urgent)   |
 *   | degraded    | 1    | degraded                    |
 *   | stale       | 2    | stale (UI-derived)          |
 *   | unknown     | 3    | no recent contact           |
 *   | healthy     | 4    | nominal                     |
 *
 * The storyboard names the order **offline → degraded → unknown → healthy**;
 * ``stale`` (a UI-derived overlay with no SAPIENT enum, currently unused by the
 * Platforms panel) is slotted between ``degraded`` and ``unknown`` — adjacent to
 * the degraded states it most resembles — so the rank is total over every
 * :type:`MarkerStatus` and the panel's existing
 * :func:`map/marker-styling.healthStateToMarkerStatus` derivation can be reused
 * verbatim as the sort key (no parallel health derivation, CLAUDE.md §4.4). The
 * sort is on the **derived category**, never the raw enum int (REQ-UI-110).
 *
 * @param status - The marker status category.
 * @returns The severity rank (0 = most urgent).
 */
export function healthSeverityRank(status: MarkerStatus): number {
  switch (status) {
    case "unhealthy":
      return 0;
    case "degraded":
      return 1;
    case "stale":
      return 2;
    case "unknown":
      return 3;
    case "healthy":
      return 4;
  }
}
